
import { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ExperiencePoint {
  icon: string;
  translationKey: string;
}

/**
 * Animated experience point that appears when scrolled into view
 */
function AnimatedExperiencePoint({ point, index }: { point: ExperiencePoint; index: number }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -20px 0px' }
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${index * 100}ms` }}
      className={`flex items-start space-x-3 transition-all duration-600
        ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}
    >
      <div className="w-6 h-6 flex items-center justify-center bg-[#6B8E23] rounded-full mt-0.5 flex-shrink-0">
        <i className={`${point.icon} text-white text-sm`} aria-hidden="true"></i>
      </div>
      <p className="text-sm sm:text-base text-[#333333] leading-relaxed">{t(point.translationKey)}</p>
    </div>
  );
}

/**
 * Company Experience Section - showcases 30+ years of experience and company story
 */
export default function CompanyExperience() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLElement>(null);
  const [sectionVisible, setSectionVisible] = useState(false);

  const experiencePoints: ExperiencePoint[] = [
    {
      icon: 'ri-time-line',
      translationKey: 'experience.backing.item1'
    },
    {
      icon: 'ri-team-line',
      translationKey: 'experience.backing.item2'
    },
    {
      icon: 'ri-shield-check-line',
      translationKey: 'experience.backing.item3'
    },
    {
      icon: 'ri-global-line',
      translationKey: 'experience.backing.item4'
    },
    {
      icon: 'ri-settings-3-line',
      translationKey: 'experience.backing.item5'
    }
  ];

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    if (typeof IntersectionObserver === 'undefined') {
      setSectionVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setSectionVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-16 sm:py-24 relative overflow-hidden bg-gradient-to-b from-[#f8faf5] to-white"
    >
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-5"
        style={{
          backgroundImage: 'url(https://public.readdy.ai/ai/img_res/d26b9de987a1980891983e7acb98cff4.jpg)'
        }}
        aria-hidden="true"
      />

      <div className="max-w-7xl mx-auto px-5 sm:px-6 relative z-10">
        {/* Header */}
        <div className={`text-center mb-12 sm:mb-16 transition-all duration-800 ${sectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center bg-[#6B8E23]/10 rounded-full px-4 py-2 mb-6">
            <i className="ri-award-line text-[#6B8E23] text-lg mr-2" aria-hidden="true"></i>
            <span className="text-[#6B8E23] font-semibold text-sm">{t('experience.tag')}</span>
          </div>
          
          <h2 className="text-3xl sm:text-5xl md:text-6xl font-bold text-[#1A1A1A] mb-4 sm:mb-6">
            {t('experience.title')}
          </h2>
          
          <p className="text-base sm:text-xl text-[#555555] max-w-4xl mx-auto leading-relaxed">
            {t('experience.subtitle')}
          </p>
        </div>

        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left Side - Experience Story */}
          <div className={`transition-all duration-800 delay-200 ${sectionVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg border border-[#E8F5E9]">
              <h3 className="text-xl sm:text-2xl font-bold text-[#1A1A1A] mb-6">
                {t('experience.story.title')}
              </h3>
              
              <p className="text-sm sm:text-base text-[#333333] leading-relaxed mb-8">
                {t('experience.story')}
              </p>

              <h4 className="text-lg sm:text-xl font-semibold text-[#6B8E23] mb-6">
                {t('experience.backing.title')}
              </h4>

              <div className="space-y-4">
                {experiencePoints.map((point, index) => (
                  <AnimatedExperiencePoint key={index} point={point} index={index} />
                ))}
              </div>
            </div>
          </div>

          {/* Right Side - Who We Are */}
          <div className={`transition-all duration-800 delay-400 ${sectionVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <div className="bg-gradient-to-br from-[#03932a] to-[#027a22] rounded-2xl p-6 sm:p-8 shadow-lg text-white">
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center mr-3">
                  <i className="ri-building-line text-white text-xl" aria-hidden="true"></i>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold">
                  {t('experience.who.title')}
                </h3>
              </div>

              <div className="space-y-4 text-sm sm:text-base leading-relaxed">
                <p>
                  {t('experience.who.desc1')}
                </p>

                <p>
                  {t('experience.who.desc2')}
                </p>

                <p>
                  {t('experience.who.desc3')}
                </p>
              </div>

              {/* Highlight Icons */}
              <div className="grid grid-cols-2 gap-4 mt-8 pt-6 border-t border-white/20">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                    <i className="ri-plant-line text-white text-sm" aria-hidden="true"></i>
                  </div>
                  <span className="text-sm font-medium">{t('experience.icon.producers')}</span>
                </div>
                
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                    <i className="ri-truck-line text-white text-sm" aria-hidden="true"></i>
                  </div>
                  <span className="text-sm font-medium">{t('experience.icon.export')}</span>
                </div>
                
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                    <i className="ri-award-line text-white text-sm" aria-hidden="true"></i>
                  </div>
                  <span className="text-sm font-medium">{t('experience.icon.quality')}</span>
                </div>
                
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                    <i className="ri-route-line text-white text-sm" aria-hidden="true"></i>
                  </div>
                  <span className="text-sm font-medium">{t('experience.icon.traceability')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
